import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from "react";
import CategoryGrid from "../Shop/CategoryGrid";
import ProductGrid from "../Shop/ProductGrid";
import LoaderLuxe from "../LoaderLuxe";

/**
 * ============================================================
 * GUIDE — TUILE SPÉCIALE "CAVE"
 * ============================================================
 *
 * Cette tuile n’est pas une vraie catégorie produit.
 * Elle sert uniquement de lien visuel vers la page "alcool".
 */
const CAVE_TILE = {
  id: "cave-link",
  name: "Cave",
  label: "Cave",
  category: "Cave",
  image: null,
  isCaveLink: true,
};

/**
 * ============================================================
 * GUIDE — HELPERS DE LECTURE
 * ============================================================
 *
 * On uniformise la lecture des labels / catégories car selon
 * les sources, la valeur peut arriver sous plusieurs formes :
 * - string
 * - objet catégorie
 * - produit
 */
const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normalizeLower = (value) => normalizeLabel(value).toLowerCase();

const isCaveCategoryLike = (value) => normalizeLower(value) === "cave";

/**
 * ============================================================
 * GUIDE — HELPERS TYPE DE LAIT
 * ============================================================
 *
 * Ce helper garde le front tolérant :
 * - si type_lait absent => ""
 * - si valeur invalide => on garde la string nettoyée
 *
 * Le backend reste la vraie source de vérité métier.
 */
const normalizeMilkType = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const BoutiqueContent = ({
  vueActuelle,
  categorieActive,
  categoriesAffichees,
  catLoading,
  setCategorieActive,
  setVueActuelle,
  t,
  produitsAffiches,
  ajouterAuPanier,
  isProfilReady,
  profilPricing,
  handleProductClick,
  isLoading,
}) => {
  /**
   * ============================================================
   * GUIDE — MÉMO PROFIL
   * ============================================================
   *
   * Conservé pour rester aligné avec ta logique existante.
   */
  const prevProfilRef = useRef();

  useEffect(() => {
    if (
      JSON.stringify(prevProfilRef.current) !== JSON.stringify(profilPricing)
    ) {
      prevProfilRef.current = profilPricing;
    }
  }, [profilPricing]);

  /**
   * ============================================================
   * GUIDE — ÉTAT LOCAL FILTRE TYPE DE LAIT
   * ============================================================
   *
   * On garde cet état local ici pour un patch faible risque :
   * - pas besoin de refactor App.js
   * - pas besoin de recâbler useAppLogic
   * - le filtre reste limité à l’affichage boutique
   *
   * IMPORTANT :
   * - on l’applique uniquement aux produits classiques
   * - la cave n’est jamais concernée
   */
  const [typeLaitActif, setTypeLaitActif] = useState("");

  /**
   * ============================================================
   * GUIDE — RESET AUTOMATIQUE DU FILTRE
   * ============================================================
   *
   * Quand on passe sur la vue alcool, on reset le filtre lait.
   * On évite ainsi tout état résiduel hors périmètre.
   */
  useEffect(() => {
    if (vueActuelle === "alcool") {
      setTypeLaitActif("");
    }
  }, [vueActuelle]);

  /**
   * ============================================================
   * GUIDE — EXTRACTION D’UN LABEL
   * ============================================================
   *
   * Ce helper lit proprement une catégorie ou un item.
   */
  const getCategoryLabel = useCallback((item) => {
    if (typeof item === "string") return normalizeLabel(item);
    if (!item || typeof item !== "object") return "";

    return normalizeLabel(
      item.category || item.name || item.label || item.title || ""
    );
  }, []);

  /**
   * ============================================================
   * GUIDE — SÉCURISATION DE LA LISTE DE PRODUITS
   * ============================================================
   */
  const allProducts = useMemo(
    () => (Array.isArray(produitsAffiches) ? produitsAffiches : []),
    [produitsAffiches]
  );

  /**
   * ============================================================
   * GUIDE — SÉPARATION CLASSIQUE / CAVE
   * ============================================================
   *
   * On s’appuie sur __isCave, injecté plus haut dans App.js.
   */
  const caveProducts = useMemo(
    () => allProducts.filter((p) => p?.__isCave === true),
    [allProducts]
  );

  const normalProducts = useMemo(
    () => allProducts.filter((p) => p?.__isCave !== true),
    [allProducts]
  );

  /**
   * ============================================================
   * GUIDE — ACTIVATION MÉTIER DU FILTRE LAIT
   * ============================================================
   *
   * IMPORTANT :
   * Le filtre ne doit s’appliquer que sur les vues où on a décidé
   * de le supporter fonctionnellement.
   *
   * Ici on l’autorise sur les vues classiques suivantes :
   * - magasin
   * - produits
   * - actions
   *
   * On l’exclut de :
   * - alcool
   */
  const canApplyMilkFilter =
    vueActuelle === "magasin" ||
    vueActuelle === "produits" ||
    vueActuelle === "actions";

  /**
   * ============================================================
   * GUIDE — FILTRE TYPE DE LAIT (CLASSIQUE UNIQUEMENT)
   * ============================================================
   *
   * Le filtre lait s’applique uniquement :
   * - aux produits classiques
   * - aux vues autorisées
   *
   * Ainsi on évite qu’un état local persiste silencieusement sur
   * une vue qui ne devrait pas être impactée.
   */
  const filteredNormalProductsByMilk = useMemo(() => {
    const milk = normalizeMilkType(typeLaitActif);

    if (!canApplyMilkFilter) return normalProducts;
    if (!milk) return normalProducts;

    return normalProducts.filter(
      (p) => normalizeMilkType(p?.type_lait) === milk
    );
  }, [normalProducts, typeLaitActif, canApplyMilkFilter]);

  /**
   * ============================================================
   * GUIDE — LISTE DES VRAIES CATÉGORIES CAVE
   * ============================================================
   *
   * Exemple :
   * - Vin rouge
   * - Champagne
   * - Alcool fort
   *
   * Cette liste sert à :
   * - afficher les catégories dans AlcoolView
   * - enlever ces catégories des pages classiques
   */
  const caveCategoryNames = useMemo(() => {
    return Array.from(
      new Set(
        caveProducts
          .map((p) => getCategoryLabel(p?.category || p))
          .filter(Boolean)
      )
    );
  }, [caveProducts, getCategoryLabel]);

  /**
   * ============================================================
   * GUIDE — PRODUITS À AFFICHER
   * ============================================================
   *
   * Cas 1 — Page cave :
   * - sans catégorie active => tous les produits cave
   * - avec catégorie active => seulement la catégorie cave choisie
   *
   * Cas 2 — Pages classiques :
   * - si categorieActive = "Cave" => aucun produit, car c’est un lien
   * - sinon uniquement produits classiques
   *
   * IMPORTANT :
   * - le filtre type_lait s’applique ici uniquement au flux classique
   */
  const safeProducts = useMemo(() => {
    // ==========================================================
    // PAGE CAVE
    // ==========================================================
    if (vueActuelle === "alcool") {
      if (!categorieActive) return caveProducts;

      return caveProducts.filter(
        (p) => getCategoryLabel(p?.category) === normalizeLabel(categorieActive)
      );
    }

    // ==========================================================
    // TUILE CAVE DANS MAGASIN / PRODUITS
    // ==========================================================
    if (isCaveCategoryLike(categorieActive)) {
      return [];
    }

    // ==========================================================
    // PAGES CLASSIQUES
    // ==========================================================
    if (!categorieActive) return filteredNormalProductsByMilk;

    return filteredNormalProductsByMilk.filter(
      (p) => getCategoryLabel(p?.category) === normalizeLabel(categorieActive)
    );
  }, [
    vueActuelle,
    categorieActive,
    caveProducts,
    filteredNormalProductsByMilk,
    getCategoryLabel,
  ]);

  /**
   * ============================================================
   * GUIDE — CATÉGORIES À AFFICHER
   * ============================================================
   *
   * PAGE alcool :
   * - on remplace complètement les catégories par les catégories cave
   *
   * PAGE magasin / produits :
   * - on enlève les vraies catégories cave
   * - on ajoute une seule tuile "Cave" si des produits cave existent
   *
   * PAGE actions :
   * - aucune injection spéciale
   */
  const computedCategories = useMemo(() => {
    const safeCats = Array.isArray(categoriesAffichees)
      ? categoriesAffichees
      : [];

    // ==========================================================
    // PAGE CAVE
    // ==========================================================
    if (vueActuelle === "alcool") {
      return caveCategoryNames.map((cat) => ({
        id: `cave-${cat}`,
        name: cat,
        label: cat,
        category: cat,
      }));
    }

    // ==========================================================
    // PAGES CLASSIQUES AVEC TUILES
    // ==========================================================
    if (vueActuelle === "magasin" || vueActuelle === "produits") {
      const filteredNormalCats = safeCats.filter((cat) => {
        const label = getCategoryLabel(cat);

        // on supprime :
        // - les catégories cave réelles
        // - une éventuelle catégorie "Cave" venant d’ailleurs
        return (
          label &&
          !caveCategoryNames.includes(label) &&
          !isCaveCategoryLike(label)
        );
      });

      const hasCaveProducts = caveProducts.length > 0;

      return hasCaveProducts
        ? [CAVE_TILE, ...filteredNormalCats]
        : filteredNormalCats;
    }

    // ==========================================================
    // AUTRES PAGES
    // ==========================================================
    return safeCats;
  }, [
    categoriesAffichees,
    caveCategoryNames,
    caveProducts.length,
    vueActuelle,
    getCategoryLabel,
  ]);

  /**
   * ============================================================
   * GUIDE — ACTIVATION UI DU FILTRE LAIT
   * ============================================================
   *
   * On affiche le filtre sur les vues classiques où il a du sens.
   * Ici on l’aligne sur le périmètre métier choisi.
   */
  const afficherFiltreLait = canApplyMilkFilter;

  /**
   * ============================================================
   * GUIDE — LOADER
   * ============================================================
   */
  if (isLoading && allProducts.length === 0) {
    return <LoaderLuxe />;
  }

  /**
   * ============================================================
   * GUIDE — AFFICHAGE DES TUILES CATÉGORIES
   * ============================================================
   *
   * On affiche des tuiles seulement sur :
   * - magasin
   * - produits
   * - alcool
   *
   * et seulement s’il n’y a pas déjà une catégorie active.
   */
  const doitAfficherTuiles =
    (vueActuelle === "magasin" ||
      vueActuelle === "produits" ||
      vueActuelle === "alcool") &&
    !categorieActive;

  if (doitAfficherTuiles) {
    return (
      <CategoryGrid
        categoriesAffichees={computedCategories}
        catLoading={catLoading}
        setCategorieActive={(cat) => {
          const label = getCategoryLabel(cat);
          const isCaveLink =
            cat?.isCaveLink === true || isCaveCategoryLike(label);

          /**
           * Si on clique la tuile spéciale "Cave",
           * on ouvre la page AlcoolView au lieu d’ouvrir une vraie catégorie.
           */
          if (isCaveLink) {
            setVueActuelle?.("alcool");
            setCategorieActive?.(null);
            return;
          }

          setCategorieActive?.(label);
        }}
        t={t}
        produits={safeProducts}
        vueActuelle={vueActuelle}
        categorieActive={categorieActive}
        typeLaitActif={typeLaitActif}
        setTypeLaitActif={setTypeLaitActif}
        afficherFiltreLait={afficherFiltreLait}
      />
    );
  }

  /**
   * ============================================================
   * GUIDE — ÉTAT VIDE
   * ============================================================
   */
  if (!isLoading && safeProducts.length === 0) {
    return (
      <div
        className="no-products-message"
        style={{ textAlign: "center", padding: "2rem", color: "var(--gold)" }}
      >
        <p>
          {t?.noProductsFound ||
            "Aucun produit disponible dans cette sélection."}
        </p>

        <button
          type="button"
          onClick={() => {
            setCategorieActive?.(null);
            setTypeLaitActif("");
          }}
          className="auth-link-btn"
        >
          {t?.backToCollection || "Retour à la collection"}
        </button>
      </div>
    );
  }

  /**
   * ============================================================
   * GUIDE — GRILLE PRODUITS
   * ============================================================
   */
  return (
    <ProductGrid
      produitsAffiches={safeProducts}
      categorieActive={categorieActive}
      t={t}
      vueActuelle={vueActuelle}
      ajouterAuPanier={ajouterAuPanier}
      isProfilReady={isProfilReady}
      profilPricing={profilPricing}
      onProductClick={handleProductClick}
    />
  );
};

export default BoutiqueContent;
